import React, { useState, useEffect, useMemo } from "react";
import { useTableSorting } from "@/hooks/useTableSorting";
import { SortableHeader } from "@/components/ui/sortable-header";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export interface ColumnDef {
  id: string;
  header: string;
  accessorKey?: string;
  cell?: (row: any) => React.ReactNode;
  sortable?: boolean;
  minWidth?: number;
  width?: number;
  hide?: boolean;
  editable?: boolean;
  inputType?: "text" | "number" | "date" | "select" | "multiselect" | "url" | "textarea";
  selectOptions?: string[] | { label: string; value: string }[] | ((row: any) => string[]);
  freezeLeft?: boolean;
  enableColumnFilter?: boolean;
  filterFn?: any;
  filterSelectOptions?: string[];
}

interface CustomTableProps {
  data: any[];
  columns: ColumnDef[];
  loading?: boolean;
  enableRowSelection?: boolean;
  selectedRows?: string[];
  onRowSelectionChange?: (rowIds: string[]) => void;
  onRowEdit?: (newRow: any, oldRow: any) => Promise<any>;
  enableColumnResizing?: boolean;
  onColumnVisibilityChange?: (newVisibility: Record<string, boolean>) => void;
  defaultColumnVisibility?: Record<string, boolean>;
  onColumnWidthChange?: (columnKey: string, newWidth: number) => void;
  currentColumnWidths?: Record<string, number>;
  enableSorting?: boolean;
  checkboxSelection?: boolean;
  showPagination?: boolean;
  // Database sorting props
  dbSortColumn?: string | null;
  dbSortDirection?: 'asc' | 'desc' | null;
  onDbSort?: (column: string | null, direction: 'asc' | 'desc' | null) => void;
  disableClientSorting?: boolean;
}

export function CustomTable({
  data,
  columns,
  loading = false,
  enableRowSelection = false,
  selectedRows = [],
  onRowSelectionChange,
  onRowEdit,
  enableColumnResizing = false,
  onColumnVisibilityChange,
  defaultColumnVisibility,
  onColumnWidthChange,
  currentColumnWidths,
  enableSorting = false,
  checkboxSelection = false,
  showPagination = true,
  // Database sorting props
  dbSortColumn,
  dbSortDirection,
  onDbSort,
  disableClientSorting = false,
}: CustomTableProps) {
  // Use database sorting when available, otherwise fall back to client sorting
  const shouldUseClientSorting = enableSorting && !disableClientSorting && !onDbSort;
  
  const { sortState, handleSort: handleClientSort, sortedData } = useTableSorting({
    data,
    defaultSort: { column: null, direction: null }
  });

  // Use sorted data for client-side sorting, original data for database sorting
  const displayData = shouldUseClientSorting ? sortedData : data;

  // Handle sorting - either client-side or database-side
  const handleSort = (column: string) => {
    console.log('🔍 CustomTable: handleSort called with column:', column);
    console.log('🔍 Available columns:', columns.map(col => ({ id: col.id, accessorKey: col.accessorKey, sortable: col.sortable })));
    
    if (onDbSort) {
      // Database sorting
      console.log('📊 Using database sorting for column:', column);
      console.log('📊 Current sort state - column:', dbSortColumn, 'direction:', dbSortDirection);
      
      // Map frontend column ID to database column name
      const columnConfig = columns.find(col => col.id === column);
      const dbColumnName = columnConfig?.accessorKey || column;
      
      console.log('🔄 Column mapping:', { frontendId: column, dbColumnName, columnConfig });
      
      if (dbSortColumn === dbColumnName) {
        // Cycle through: asc -> desc -> null
        const newDirection = dbSortDirection === 'asc' ? 'desc' : 
                           dbSortDirection === 'desc' ? null : 'asc';
        console.log('🔄 Cycling sort direction to:', newDirection);
        onDbSort(newDirection ? dbColumnName : null, newDirection);
      } else {
        // New column, start with ascending
        console.log('🆕 New column sort, starting with asc for:', dbColumnName);
        onDbSort(dbColumnName, 'asc');
      }
    } else if (shouldUseClientSorting) {
      // Client sorting
      console.log('💻 Using client-side sorting for column:', column);
      handleClientSort(column);
    } else {
      console.log('⚠️ No sorting handler available for column:', column);
    }
  };

  // Get current sort state for display - map back to frontend column ID
  const getCurrentSortForDisplay = () => {
    if (onDbSort && dbSortColumn) {
      // Find the frontend column ID that matches the database column name
      const matchingColumn = columns.find(col => col.accessorKey === dbSortColumn || col.id === dbSortColumn);
      return {
        column: matchingColumn?.id || dbSortColumn,
        direction: dbSortDirection
      };
    }
    return sortState;
  };

  const currentSort = getCurrentSortForDisplay();
  console.log('🎯 Current sort display state:', currentSort);

  const handleRowSelection = (rowId: string, checked: boolean) => {
    if (!onRowSelectionChange) return;
    
    const newSelectedRows = checked
      ? [...selectedRows, rowId]
      : selectedRows.filter(id => id !== rowId);
    
    onRowSelectionChange(newSelectedRows);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onRowSelectionChange) return;
    
    const newSelectedRows = checked 
      ? displayData.map(row => row.id)
      : [];
    
    onRowSelectionChange(newSelectedRows);
  };

  const isAllSelected = displayData.length > 0 && selectedRows.length === displayData.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < displayData.length;

  // Filter visible columns
  const visibleColumns = columns.filter(col => {
    if (defaultColumnVisibility && defaultColumnVisibility[col.id] === false) {
      return false;
    }
    return !col.hide;
  });

  // Enable checkbox selection if either checkboxSelection is true OR enableRowSelection is true
  const showCheckboxes = checkboxSelection || enableRowSelection;

  return (
    <div className="relative w-full">
      <TableLoadingOverlay isLoading={loading} />
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border">
              {showCheckboxes && (
                <th className="w-12 p-0 bg-muted/30">
                  <div className="flex items-center justify-center h-6 py-1">
                    <Checkbox
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) {
                          const input = el.querySelector('input') as HTMLInputElement;
                          if (input) {
                            input.indeterminate = isIndeterminate;
                          }
                        }
                      }}
                      onCheckedChange={(checked) => handleSelectAll(checked === true)}
                      aria-label="Select all rows"
                    />
                  </div>
                </th>
              )}
              {visibleColumns.map((column) => (
                <th
                  key={column.id}
                  className={cn(
                    "bg-muted/30",
                    column.freezeLeft && "sticky left-0 z-10"
                  )}
                  style={{
                    width: currentColumnWidths?.[column.id] || column.width || 'auto',
                    minWidth: column.minWidth || 100
                  }}
                >
                  {enableSorting && column.sortable !== false ? (
                    <SortableHeader
                      column={column.id}
                      currentSort={currentSort}
                      onSort={handleSort}
                    >
                      {column.header}
                    </SortableHeader>
                  ) : (
                    <div className="px-4 py-0.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {column.header}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, rowIndex) => (
              <tr
                key={row.id || rowIndex}
                className="border-b border-border hover:bg-muted/50 transition-colors"
              >
                {showCheckboxes && (
                  <td className="w-12 p-0">
                    <div className="flex items-center justify-center h-6 py-1">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onCheckedChange={(checked) => handleRowSelection(row.id, checked === true)}
                        aria-label={`Select row ${rowIndex + 1}`}
                      />
                    </div>
                  </td>
                )}
                {visibleColumns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      "px-4 py-0.5 text-sm",
                      column.freezeLeft && "sticky left-0 z-10 bg-background"
                    )}
                    style={{
                      width: currentColumnWidths?.[column.id] || column.width || 'auto',
                      minWidth: column.minWidth || 100
                    }}
                  >
                    {column.cell ? 
                      column.cell(row) : 
                      (column.accessorKey ? row[column.accessorKey] : '')
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {displayData.length === 0 && !loading && (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          No data available
        </div>
      )}
    </div>
  );
}
